<template>
  <div class="login-container">
    <div class="login-card">
      <div class="login-header">
        <h1>Acceso</h1>
        <p class="subtitle">Introduce tus credenciales para acceder</p>
      </div>

      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label for="username">Usuario o Email</label>
          <input
            type="text"
            id="username"
            v-model="username"
            placeholder="usuario@ejemplo.com"
            :class="{ 'input-error': errors.username }"
            @focus="clearError('username')"
            required
          />
          <span v-if="errors.username" class="error-message">{{
            errors.username
          }}</span>
        </div>

        <div class="form-group">
          <label for="password">Contraseña</label>
          <div class="password-input">
            <input
              :type="showPassword ? 'text' : 'password'"
              id="password"
              v-model="password"
              placeholder="Tu contraseña"
              :class="{ 'input-error': errors.password }"
              @focus="clearError('password')"
              required
            />
            <button
              type="button"
              class="toggle-password"
              @click="showPassword = !showPassword"
            >
              {{ showPassword ? "Ocultar" : "Mostrar" }}
            </button>
          </div>
          <span v-if="errors.password" class="error-message">{{
            errors.password
          }}</span>
        </div>

        <button type="submit" class="login-button">Iniciar Sesión</button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from "vue";
import { useAuth } from "@/store/auth"
import router from "@/router";

const store = useAuth()

// Form data
const username = ref("");
const password = ref("");

// Form validation
const errors = reactive({
  username: "",
  password: "",
});

const clearError = field => {
  errors[field] = "";
};

const validateForm = () => {
  let isValid = true;

  // Validate username
  if (!username.value.trim()) {
    errors.username = "El usuario es obligatorio";
    isValid = false;
  } else if (username.value.includes("@") && !isValidEmail(username.value)) {
    errors.username = "Introduce un email válido";
    isValid = false;
  }

  // Validate password
  if (!password.value) {
    errors.password = "La contraseña es obligatoria";
    isValid = false;
  } else if (password.value.length < 6) {
    errors.password = "La contraseña debe tener al menos 6 caracteres";
    isValid = false;
  }

  return isValid;
};

const isValidEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const handleLogin = async () => {
  if (validateForm()) {
    const response = await store.login(username.value, password.value);

    if (response) {
      router.push({ name: "home" });
    }
  }
};
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 20px;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.login-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 420px;
  padding: 30px;
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
}

.login-header h1 {
  margin: 0;
  color: #2c3e50;
  font-size: 2rem;
}

.subtitle {
  color: #7f8c8d;
  margin-top: 5px;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

label {
  font-weight: 500;
  color: #2c3e50;
  font-size: 0.95rem;
}

input[type="text"],
input[type="password"] {
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s;
}

input:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.2);
}

.input-error {
  border-color: #e74c3c !important;
}

.error-message {
  color: #e74c3c;
  font-size: 0.85rem;
}

.password-input {
  position: relative;
  display: flex;
}

.password-input input {
  flex: 1;
  padding-right: 80px;
}

.toggle-password {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #7f8c8d;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 5px 10px;
}

.toggle-password:hover {
  color: #42b983;
}

.remember-forgot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.forgot-password {
  color: #42b983;
  text-decoration: none;
}

.forgot-password:hover {
  text-decoration: underline;
}

.login-button {
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
}

.login-button:hover {
  background-color: #3aa876;
}

.login-footer {
  margin-top: 25px;
  text-align: center;
  font-size: 0.95rem;
}

.login-footer a {
  color: #42b983;
  text-decoration: none;
  font-weight: 600;
}

.login-footer a:hover {
  text-decoration: underline;
}

@media (max-width: 480px) {
  .login-card {
    padding: 20px;
  }

  .remember-forgot {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
